import React, { useState, useEffect } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { Armchair, BellRing, Radio, Wifi, WifiOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const SmartView = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [seats, setSeats] = useState([]);
  const [sections, setSections] = useState([]);
  const [seatTypes, setSeatTypes] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [liveConnection, setLiveConnection] = useState('connecting');

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      fetchAllData(parsedUser.entityId);
      
      // Auto-refresh every 30 seconds
      const dataInterval = setInterval(() => {
        fetchAllData(parsedUser.entityId);
      }, 30000);
      
      // Update current time every second for calling timers
      const timeInterval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
      
      return () => {
        clearInterval(dataInterval);
        clearInterval(timeInterval);
      };
    }
  }, []);

  useEffect(() => {
    if (!user?.entityId) return undefined;

    const source = new EventSource(`${BACKEND_URL}/api/allocations/${user.entityId}/calling-stream`);

    const handleCalls = (event) => {
      const liveCalls = JSON.parse(event.data).allocations || [];
      const liveById = new Map(liveCalls.map((allocation) => [allocation.id, allocation]));

      setAllocations((current) => {
        const currentIds = new Set(current.map((allocation) => allocation.id));
        const updated = current.map((allocation) => {
          if (liveById.has(allocation.id)) return liveById.get(allocation.id);
          if (allocation.callingFlag !== 'Non Calling') {
            return { ...allocation, callingFlag: 'Non Calling' };
          }
          return allocation;
        });

        liveCalls.forEach((allocation) => {
          if (!currentIds.has(allocation.id)) updated.push(allocation);
        });

        return updated;
      });
      setLiveConnection('connected');
    };

    source.addEventListener('calls', handleCalls);
    source.onopen = () => setLiveConnection('connected');
    source.onerror = () => setLiveConnection('reconnecting');

    return () => {
      source.removeEventListener('calls', handleCalls);
      source.close();
    };
  }, [user?.entityId]);

  const fetchAllData = async (propertyId) => {
    try {
      setLoading(true);
      
      // Fetch seats
      const seatsResponse = await axios.get(`${BACKEND_URL}/api/seats/${propertyId}`);
      if (seatsResponse.data.success) {
        setSeats(seatsResponse.data.seats);
      }

      // Fetch sections
      const groupsResponse = await axios.get(`${BACKEND_URL}/api/sections/${propertyId}`);
      if (groupsResponse.data.success) {
        setSections(groupsResponse.data.sections);
      }

      // Fetch seat types
      const seatTypesResponse = await axios.get(`${BACKEND_URL}/api/seat-types/${propertyId}`);
      if (seatTypesResponse.data.success) {
        setSeatTypes(seatTypesResponse.data.seatTypes);
      }

      // Fetch all non-complete allocations (regardless of date)
      const allocationsResponse = await axios.get(`${BACKEND_URL}/api/allocations/${propertyId}`);
      if (allocationsResponse.data.success) {
        // Filter for non-complete allocations only (show active allocations regardless of date)
        const activeAllocations = allocationsResponse.data.allocations.filter(
          alloc => alloc.status !== 'Complete'
        );
        setAllocations(activeAllocations);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get seat status and allocation details
  const getSeatStatus = (seatId) => {
    // Find if this seat is in any active allocation
    const allocation = allocations.find(alloc => 
      alloc.seatIds.includes(seatId) && alloc.status !== 'Complete'
    );
    
    if (!allocation) {
      return { status: 'Free', color: 'bg-white border-slate-300', allocation: null, isCalling: false, callingDuration: 0 };
    }
    
    // Check if calling flag is set
    const isCalling = allocation.callingFlag === "Calling" || allocation.callingFlag === "Calling for Checkout";
    
    // Calculate calling duration in seconds
    let callingDuration = 0;
    if (isCalling && allocation.updatedAt) {
      const updatedAt = new Date(allocation.updatedAt);
      callingDuration = Math.floor((currentTime - updatedAt.getTime()) / 1000);
    }
    
    // Determine color based on calling duration
    let color;
    if (isCalling) {
      if (callingDuration > 90) {
        // Red for > 90 seconds
        color = 'bg-red-500 border-red-600';
      } else if (callingDuration > 45) {
        // Orange for > 45 seconds
        color = 'bg-orange-500 border-orange-600';
      } else if (callingDuration > 15) {
        // Yellow for > 15 seconds
        color = 'bg-yellow-500 border-yellow-600';
      } else {
        // Default calling color (< 15 seconds)
        color = 'bg-blue-400 border-blue-500';
      }
    } else {
      // Return status based on allocation status when not calling
      const statusMap = {
        'Allocated': { status: 'Allocated', color: 'bg-blue-500 border-blue-600' },
        'Active': { status: 'Active', color: 'bg-green-500 border-green-600' },
        'Billing': { status: 'Billing', color: 'bg-purple-500 border-purple-600' },
        'Clear': { status: 'Clear', color: 'bg-teal-500 border-teal-600' },
        'Complete': { status: 'Free', color: 'bg-white border-slate-300' }
      };
      
      const statusInfo = statusMap[allocation.status] || { status: 'Free', color: 'bg-white border-slate-300' };
      color = statusInfo.color;
    }
    
    return { 
      status: allocation.status,
      color: color,
      allocation: allocation,
      isCalling: isCalling,
      callingDuration: callingDuration
    };
  };

  // Calculate statistics
  const calculateStats = () => {
    const total = seats.length;
    let available = 0;  // Free
    let allocatedSeats = 0;  // Allocated (Reserved)
    let activeSeats = 0;     // Active (Occupied)
    let billingSeats = 0;    // Billing
    let clearSeats = 0;      // Clear

    seats.forEach(seat => {
      const { status } = getSeatStatus(seat.id);
      
      if (status === 'Free') {
        available++;
      } else if (status === 'Allocated') {
        allocatedSeats++;
      } else if (status === 'Active') {
        activeSeats++;
      } else if (status === 'Billing') {
        billingSeats++;
      } else if (status === 'Clear') {
        clearSeats++;
      }
    });

    // Count allocations per status
    const allocatedCount = allocations.filter(a => a.status === 'Allocated').length;
    const activeCount = allocations.filter(a => a.status === 'Active').length;
    const billingCount = allocations.filter(a => a.status === 'Billing').length;
    const clearCount = allocations.filter(a => a.status === 'Clear').length;

    return { 
      total, 
      available, 
      allocated: { seats: allocatedSeats, allocations: allocatedCount },
      active: { seats: activeSeats, allocations: activeCount },
      billing: { seats: billingSeats, allocations: billingCount },
      clear: { seats: clearSeats, allocations: clearCount }
    };
  };

  const stats = calculateStats();

  // Calculate calling duration
  const getCallingSince = (allocation) => {
    if (!allocation || allocation.callingFlag === "Non Calling") return null;
    
    const updatedAt = new Date(allocation.updatedAt);
    const elapsedSeconds = Math.floor((currentTime - updatedAt.getTime()) / 1000);
    
    const flagText = allocation.callingFlag === "Calling" ? "Calling" : "Checkout";
    
    // Format time as Xm Ys if >= 60 seconds
    let timeStr;
    if (elapsedSeconds >= 60) {
      const minutes = Math.floor(elapsedSeconds / 60);
      const seconds = elapsedSeconds % 60;
      timeStr = `${minutes}m ${seconds}s`;
    } else {
      timeStr = `${elapsedSeconds}s`;
    }
    
    return `${flagText} ${timeStr}`;
  };

  // Helper function to sort seats by seat number (handles numeric and alphanumeric)
  const sortSeats = (seatsArray) => {
    return [...seatsArray].sort((a, b) => {
      const aNum = parseInt(a.seatNumber, 10);
      const bNum = parseInt(b.seatNumber, 10);
      
      // If both are valid numbers, sort numerically
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      
      // Otherwise sort as strings
      return a.seatNumber.localeCompare(b.seatNumber, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  // Section seats by section
  const groupedSeats = sections.map(section => {
    const groupSeats = seats.filter(seat => seat.sectionId === section.id);
    return {
      ...section,
      seats: sortSeats(groupSeats)
    };
  });

  // Seats without a section
  const ungroupedSeats = sortSeats(seats.filter(seat => !seat.sectionId));

  // Get seat type info
  const getSeatType = (seatTypeId) => {
    return seatTypes.find(st => st.id === seatTypeId) || { name: 'Seat', icon: null };
  };

  const getSeatNumbers = (allocation) => (
    (allocation.seatIds || [])
      .map((seatId) => seats.find((seat) => seat.id === seatId)?.seatNumber)
      .filter(Boolean)
      .join(', ')
  );

  const liveCalls = allocations
    .filter((allocation) => ['Calling', 'Calling for Checkout'].includes(allocation.callingFlag))
    .sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading SmartView...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">SmartView</h1>
            <p className="text-slate-600 mt-1">Real-time seat monitoring and availability</p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-slate-500">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Live calls · full sync every 30s</span>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Total Seats */}
          <div className="bg-slate-600 rounded-xl shadow-md p-4 text-center">
            <p className="text-sm text-slate-200 mb-2">Total Seats</p>
            <p className="text-3xl font-bold text-white">{stats.total}</p>
          </div>
          
          {/* Available (Free) */}
          <div className="bg-white rounded-xl shadow-md p-4 text-center border-2 border-slate-300">
            <p className="text-sm text-slate-600 mb-2">Available</p>
            <p className="text-3xl font-bold text-slate-700">{stats.available}</p>
          </div>
          
          {/* Allocated */}
          <div className="bg-blue-500 rounded-xl shadow-md p-4 text-center">
            <p className="text-sm text-blue-100 mb-2">Allocated</p>
            {stats.allocated.allocations > 0 ? (
              <>
                <p className="text-lg font-bold text-white">{stats.allocated.allocations} Allocation{stats.allocated.allocations !== 1 ? 's' : ''}</p>
                <p className="text-xs text-blue-100">({stats.allocated.seats} Seat{stats.allocated.seats !== 1 ? 's' : ''})</p>
              </>
            ) : (
              <p className="text-3xl font-bold text-white">0</p>
            )}
          </div>
          
          {/* Active (Occupied) */}
          <div className="bg-green-500 rounded-xl shadow-md p-4 text-center">
            <p className="text-sm text-green-100 mb-2">Active</p>
            {stats.active.allocations > 0 ? (
              <>
                <p className="text-lg font-bold text-white">{stats.active.allocations} Allocation{stats.active.allocations !== 1 ? 's' : ''}</p>
                <p className="text-xs text-green-100">({stats.active.seats} Seat{stats.active.seats !== 1 ? 's' : ''})</p>
              </>
            ) : (
              <p className="text-3xl font-bold text-white">0</p>
            )}
          </div>
          
          {/* Billing */}
          <div className="bg-purple-500 rounded-xl shadow-md p-4 text-center">
            <p className="text-sm text-purple-100 mb-2">Billing</p>
            {stats.billing.allocations > 0 ? (
              <>
                <p className="text-lg font-bold text-white">{stats.billing.allocations} Allocation{stats.billing.allocations !== 1 ? 's' : ''}</p>
                <p className="text-xs text-purple-100">({stats.billing.seats} Seat{stats.billing.seats !== 1 ? 's' : ''})</p>
              </>
            ) : (
              <p className="text-3xl font-bold text-white">0</p>
            )}
          </div>
          
          {/* Clear */}
          <div className="bg-teal-500 rounded-xl shadow-md p-4 text-center">
            <p className="text-sm text-teal-100 mb-2">Clear</p>
            {stats.clear.allocations > 0 ? (
              <>
                <p className="text-lg font-bold text-white">{stats.clear.allocations} Allocation{stats.clear.allocations !== 1 ? 's' : ''}</p>
                <p className="text-xs text-teal-100">({stats.clear.seats} Seat{stats.clear.seats !== 1 ? 's' : ''})</p>
              </>
            ) : (
              <p className="text-3xl font-bold text-white">0</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-10 gap-6 items-start">
          <div className="xl:col-span-7 space-y-6 min-w-0">
        {/* Legend */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Status Legend</h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-white border-2 border-slate-300 rounded"></div>
              <span className="text-sm text-slate-600">Free</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-blue-500 border-2 border-blue-600 rounded"></div>
              <span className="text-sm text-slate-600">Allocated</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-green-500 border-2 border-green-600 rounded"></div>
              <span className="text-sm text-slate-600">Active</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-purple-500 border-2 border-purple-600 rounded"></div>
              <span className="text-sm text-slate-600">Billing</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-teal-500 border-2 border-teal-600 rounded"></div>
              <span className="text-sm text-slate-600">Clear</span>
            </div>
          </div>
        </div>

        {/* Seats by Section */}
        {seats.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <Armchair className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">No Seats Available</h3>
            <p className="text-slate-600">Please add seats to see them in SmartView</p>
          </div>
        ) : (
          <>
            {/* Sectioned Seats */}
            {groupedSeats.map(section => {
              if (section.seats.length === 0) return null;
              
              return (
                <div key={section.id} className="bg-white rounded-xl shadow-md overflow-visible">
                  <div className="bg-gradient-to-r from-slate-700 to-slate-600 px-6 py-4">
                    <h2 className="text-xl font-bold text-white">{section.name}</h2>
                    <p className="text-slate-300 text-sm">{section.seats.length} seats</p>
                  </div>
                  <div className="p-6">
                    {(() => {
                      // Section seats by allocation
                      const seatsWithAllocation = [];
                      const seatsWithoutAllocation = [];
                      
                      section.seats.forEach(seat => {
                        const { allocation } = getSeatStatus(seat.id);
                        if (allocation) {
                          seatsWithAllocation.push({ seat, allocation });
                        } else {
                          seatsWithoutAllocation.push(seat);
                        }
                      });
                      
                      // Section by allocation ID
                      const allocationSections = {};
                      seatsWithAllocation.forEach(({ seat, allocation }) => {
                        if (!allocationSections[allocation.id]) {
                          allocationSections[allocation.id] = {
                            allocation,
                            seats: []
                          };
                        }
                        allocationSections[allocation.id].seats.push(seat);
                      });
                      
                      return (
                        <div className="flex flex-wrap gap-y-2 gap-x-4">
                          {/* Allocated seats grouped by allocation */}
                          {Object.values(allocationSections).map(({ allocation, seats: allocSeats }) => (
                            <div 
                              key={allocation.id}
                              className="group border-2 border-dashed border-blue-400 bg-blue-50/30 rounded-lg p-2 cursor-pointer hover:bg-blue-50/50 transition-colors inline-flex flex-col gap-2 relative section"
                              onClick={() => navigate(`/user/allocation/${allocation.id}`)}
                            >
                              <div className="flex items-center space-x-2 px-1">
                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                <span className="text-xs font-semibold text-slate-700">
                                  {allocation.guestName} - Room {allocation.roomNumber}
                                </span>
                                {allocation.guestCategory && (
                                  <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                    {allocation.guestCategory}
                                  </span>
                                )}
                              </div>
                              
                              <div className="flex flex-wrap gap-y-2 gap-x-4">
                                {allocSeats.map(seat => {
                                  const { status, color, isCalling } = getSeatStatus(seat.id);
                                  const seatType = getSeatType(seat.seatTypeId);
                                  
                                  return (
                                    <div
                                      key={seat.id}
                                      className="relative"
                                    >
                            {/* Category Badge (VIP, etc.) */}
                            {allocation && allocation.guestCategory && (
                              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-10">
                                <span className="bg-amber-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full shadow-md whitespace-nowrap">
                                  {allocation.guestCategory}
                                </span>
                              </div>
                            )}
                            
                            <div
                              onClick={() => allocation && navigate(`/user/allocation/${allocation.id}`)}
                              className={`${color} rounded-lg border-2 p-2 transition-all hover:scale-105 hover:shadow-lg ${allocation ? 'cursor-pointer' : 'cursor-default'} flex flex-col items-center justify-center h-[88px] w-[60px] ${
                                isCalling ? 'animate-pulse ring-4 ring-red-500 ring-offset-2 shadow-xl shadow-red-500/50' : ''
                              }`}
                            >
                              {seatType.icon ? (
                                <img 
                                  src={seatType.icon} 
                                  alt={seatType.name}
                                  className={`w-6 h-6 object-contain mb-1 ${
                                    status === 'Free' ? '' : 'brightness-0 invert'
                                  }`}
                                />
                              ) : (
                                <Armchair className={`w-5 h-5 mb-1 ${
                                  status === 'Free' ? 'text-slate-400' : 'text-white'
                                }`} />
                              )}
                              <span className={`text-[11px] font-semibold ${
                                status === 'Free' ? 'text-slate-700' : 'text-white'
                              }`}>
                                {seat.seatNumber}
                              </span>
                            </div>

                            {/* Calling Timer */}
                            {isCalling && (
                              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 z-10">
                                <span className="bg-red-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-lg whitespace-nowrap animate-pulse">
                                  {getCallingSince(allocation)}
                                </span>
                              </div>
                            )}
                          </div>
                                  );
                                })}
                              </div>
                              
                              {/* Allocation Hover Tooltip */}
                              <div className="absolute bottom-full left-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100]">
                                <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl min-w-[200px]">
                                  <p className="font-bold text-sm mb-1">{allocation.guestName}</p>
                                  <p className="text-slate-300 mb-2">Room {allocation.roomNumber}</p>
                                  <div className="border-t border-slate-700 pt-2 space-y-1">
                                    <p className="font-semibold text-teal-400">Status: {allocation.status}</p>
                                    {allocation.guestCategory && (
                                      <p className="text-amber-400">
                                        <span className="text-slate-400">Category:</span> {allocation.guestCategory}
                                      </p>
                                    )}
                                    <p className="text-slate-200 text-[10px] mt-1">
                                      <span className="text-slate-400">Allocated:</span> {new Date(allocation.createdAt).toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>
                                    <p className="text-slate-200 text-[10px]">
                                      <span className="text-slate-400">Seats:</span> {allocSeats.map(s => s.seatNumber).join(', ')}
                                    </p>
                                  </div>
                                </div>
                                <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900"></div>
                              </div>
                            </div>
                          ))}
                          
                          {/* Free seats (not allocated) */}
                          {seatsWithoutAllocation.map(seat => {
                            const { status, color } = getSeatStatus(seat.id);
                            const seatType = getSeatType(seat.seatTypeId);
                            
                            return (
                              <div
                                key={seat.id}
                                className="group relative"
                              >
                                <div
                                  className={`${color} rounded-lg border-2 p-2 transition-all hover:scale-105 hover:shadow-lg cursor-default flex flex-col items-center justify-center h-[88px] w-[60px]`}
                                >
                                  {seatType.icon ? (
                                    <img 
                                      src={seatType.icon} 
                                      alt={seatType.name}
                                      className="w-6 h-6 object-contain mb-1"
                                    />
                                  ) : (
                                    <Armchair className="w-5 h-5 mb-1 text-slate-400" />
                                  )}
                                  <span className="text-[11px] font-semibold text-slate-700">
                                    {seat.seatNumber}
                                  </span>
                                </div>
                                
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100]">
                                  <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl min-w-[180px]">
                                    <p className="font-bold text-sm mb-1">{seat.seatNumber}</p>
                                    <p className="text-slate-300 mb-2">{seatType.name}</p>
                                    <div className="border-t border-slate-700 pt-2">
                                      <p className="text-slate-300 italic">Available</p>
                                    </div>
                                  </div>
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900"></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}

            {/* Ungrouped Seats */}
            {ungroupedSeats.length > 0 && (
              <div className="bg-white rounded-xl shadow-md overflow-visible">
                <div className="bg-gradient-to-r from-slate-500 to-slate-400 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Ungrouped Seats</h2>
                  <p className="text-slate-200 text-sm">{ungroupedSeats.length} seats</p>
                </div>
                
                {/* Info Message */}
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mx-6 mt-6">
                  <p className="text-sm text-amber-800">
                    💡 <strong>Tip:</strong> These seats are not assigned to any section. Edit seats in the Seats page to assign them to sections like "Pool Area" or "Beach Zone" for better organization.
                  </p>
                </div>
                
                <div className="p-6">
                  {(() => {
                    // Section ungrouped seats by allocation
                    const seatsWithAllocation = [];
                    const seatsWithoutAllocation = [];
                    
                    ungroupedSeats.forEach(seat => {
                      const { allocation } = getSeatStatus(seat.id);
                      if (allocation) {
                        seatsWithAllocation.push({ seat, allocation });
                      } else {
                        seatsWithoutAllocation.push(seat);
                      }
                    });
                    
                    // Section by allocation ID
                    const allocationSections = {};
                    seatsWithAllocation.forEach(({ seat, allocation }) => {
                      if (!allocationSections[allocation.id]) {
                        allocationSections[allocation.id] = {
                          allocation,
                          seats: []
                        };
                      }
                      allocationSections[allocation.id].seats.push(seat);
                    });
                    
                    return (
                      <div className="flex flex-wrap gap-y-2 gap-x-4">
                        {/* Allocated seats grouped by allocation */}
                        {Object.values(allocationSections).map(({ allocation, seats: allocSeats }) => (
                          <div 
                            key={allocation.id}
                            className="group border-2 border-dashed border-blue-400 bg-blue-50/30 rounded-lg p-2 cursor-pointer hover:bg-blue-50/50 transition-colors inline-flex flex-col gap-2 relative section"
                            onClick={() => navigate(`/user/allocation/${allocation.id}`)}
                          >
                            <div className="flex items-center space-x-2 px-1">
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                              <span className="text-xs font-semibold text-slate-700">
                                {allocation.guestName} - Room {allocation.roomNumber}
                              </span>
                              {allocation.guestCategory && (
                                <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                  {allocation.guestCategory}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex flex-wrap gap-y-2 gap-x-4">
                              {allocSeats.map(seat => {
                                const { status, color, isCalling } = getSeatStatus(seat.id);
                                const seatType = getSeatType(seat.seatTypeId);
                                
                                return (
                                  <div
                                    key={seat.id}
                                    className="relative"
                                  >
                          {/* Category Badge (VIP, etc.) */}
                          {allocation && allocation.guestCategory && (
                            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-10">
                              <span className="bg-amber-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full shadow-md whitespace-nowrap">
                                {allocation.guestCategory}
                              </span>
                            </div>
                          )}
                          
                          <div
                            onClick={() => allocation && navigate(`/user/allocation/${allocation.id}`)}
                            className={`${color} rounded-lg border-2 p-2 transition-all hover:scale-105 hover:shadow-lg ${allocation ? 'cursor-pointer' : 'cursor-default'} flex flex-col items-center justify-center h-[88px] w-[60px] ${
                              isCalling ? 'animate-pulse ring-4 ring-red-500 ring-offset-2 shadow-xl shadow-red-500/50' : ''
                            }`}
                          >
                            {seatType.icon ? (
                              <img 
                                src={seatType.icon} 
                                alt={seatType.name}
                                className={`w-6 h-6 object-contain mb-1 ${
                                  status === 'Free' ? '' : 'brightness-0 invert'
                                }`}
                              />
                            ) : (
                              <Armchair className={`w-5 h-5 mb-1 ${
                                status === 'Free' ? 'text-slate-400' : 'text-white'
                              }`} />
                            )}
                            <span className={`text-[11px] font-semibold ${
                              status === 'Free' ? 'text-slate-700' : 'text-white'
                            }`}>
                              {seat.seatNumber}
                            </span>
                          </div>

                          {/* Calling Timer */}
                          {isCalling && (
                            <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 z-10">
                              <span className="bg-red-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-lg whitespace-nowrap animate-pulse">
                                {getCallingSince(allocation)}
                              </span>
                            </div>
                          )}
                        </div>
                                );
                              })}
                            </div>
                            
                            {/* Allocation Hover Tooltip */}
                            <div className="absolute bottom-full left-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100]">
                              <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl min-w-[200px]">
                                <p className="font-bold text-sm mb-1">{allocation.guestName}</p>
                                <p className="text-slate-300 mb-2">Room {allocation.roomNumber}</p>
                                <div className="border-t border-slate-700 pt-2 space-y-1">
                                  <p className="font-semibold text-teal-400">Status: {allocation.status}</p>
                                  {allocation.guestCategory && (
                                    <p className="text-amber-400">
                                      <span className="text-slate-400">Category:</span> {allocation.guestCategory}
                                    </p>
                                  )}
                                  <p className="text-slate-200 text-[10px] mt-1">
                                    <span className="text-slate-400">Allocated:</span> {new Date(allocation.createdAt).toLocaleString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                  <p className="text-slate-200 text-[10px]">
                                    <span className="text-slate-400">Seats:</span> {allocSeats.map(s => s.seatNumber).join(', ')}
                                  </p>
                                </div>
                              </div>
                              <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900"></div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Free seats (not allocated) */}
                        {seatsWithoutAllocation.map(seat => {
                          const { status, color } = getSeatStatus(seat.id);
                          const seatType = getSeatType(seat.seatTypeId);
                          
                          return (
                            <div
                              key={seat.id}
                              className="group relative"
                            >
                              <div
                                className={`${color} rounded-lg border-2 p-2 transition-all hover:scale-105 hover:shadow-lg cursor-default flex flex-col items-center justify-center h-[88px] w-[60px]`}
                              >
                                {seatType.icon ? (
                                  <img 
                                    src={seatType.icon} 
                                    alt={seatType.name}
                                    className="w-6 h-6 object-contain mb-1"
                                  />
                                ) : (
                                  <Armchair className="w-5 h-5 mb-1 text-slate-400" />
                                )}
                                <span className="text-[11px] font-semibold text-slate-700">
                                  {seat.seatNumber}
                                </span>
                              </div>
                              
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100]">
                                <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl min-w-[180px]">
                                  <p className="font-bold text-sm mb-1">{seat.seatNumber}</p>
                                  <p className="text-slate-300 mb-2">{seatType.name}</p>
                                  <div className="border-t border-slate-700 pt-2">
                                    <p className="text-slate-300 italic">Available</p>
                                  </div>
                                </div>
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900"></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </>
        )}
          </div>

          <aside className="xl:col-span-3 xl:sticky xl:top-6 bg-slate-900 rounded-2xl shadow-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-700 bg-gradient-to-r from-slate-900 to-slate-800">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15">
                    <BellRing className="h-5 w-5 text-amber-400" />
                    {liveCalls.length > 0 && <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-red-500 ring-2 ring-slate-900 animate-pulse" />}
                  </div>
                  <div>
                    <h2 className="font-bold text-white">Live Calling</h2>
                    <p className="text-xs text-slate-400">Updates automatically</p>
                  </div>
                </div>
                <span className="rounded-full bg-slate-700 px-2.5 py-1 text-xs font-bold text-white">{liveCalls.length}</span>
              </div>
              <div className={`mt-3 flex items-center gap-1.5 text-xs ${liveConnection === 'connected' ? 'text-emerald-400' : 'text-amber-400'}`}>
                {liveConnection === 'connected' ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
                <span>{liveConnection === 'connected' ? 'Live connection active' : 'Reconnecting…'}</span>
              </div>
            </div>

            <div className="h-[calc(150vh-22.5rem)] min-h-[24rem] max-h-[calc(150vh-22.5rem)] overflow-y-auto p-4 space-y-3">
              {liveCalls.length === 0 ? (
                <div className="py-12 px-4 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-800">
                    <Radio className="h-5 w-5 text-slate-500" />
                  </div>
                  <p className="font-semibold text-slate-300">No active calls</p>
                  <p className="mt-1 text-xs text-slate-500">New guest calls will appear here instantly.</p>
                </div>
              ) : liveCalls.map((allocation) => {
                const isCheckout = allocation.callingFlag === 'Calling for Checkout';
                return (
                  <button
                    key={allocation.id}
                    type="button"
                    onClick={() => navigate(`/user/allocation/${allocation.id}`)}
                    className={`w-full rounded-xl border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-lg ${
                      isCheckout
                        ? 'border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/15'
                        : 'border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/15'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-bold text-white">{allocation.guestName || 'Guest'}</p>
                        <p className="mt-0.5 text-sm text-slate-300">Room {allocation.roomNumber || '—'}</p>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${
                        isCheckout ? 'bg-emerald-400 text-emerald-950' : 'bg-amber-400 text-amber-950'
                      }`}>
                        {isCheckout ? 'Checkout' : 'Calling'}
                      </span>
                    </div>
                    <div className="mt-3 flex items-end justify-between gap-3 border-t border-white/10 pt-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Seat{(allocation.seatIds || []).length === 1 ? '' : 's'}</p>
                        <p className="text-sm font-semibold text-slate-200">{getSeatNumbers(allocation) || 'Not assigned'}</p>
                      </div>
                      <span className={`text-sm font-bold ${isCheckout ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {getCallingSince(allocation)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>
        </div>
      </div>
    </UserLayout>
  );
};
